import { Link } from "react-router-dom";
import { Star, ShoppingBag, Gift } from "lucide-react";
import { motion } from "framer-motion";
import { img, money } from "../lib/api";
import { useCart } from "../lib/store";

export function Stars({ value = 0, size = 14, onPick, testid }) {
  return (
    <span className="inline-flex gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} onClick={onPick ? () => onPick(i) : undefined} data-testid={onPick ? `star-pick-${i}` : undefined}
          style={{ width: size, height: size }} className={`${i <= Math.round(value) ? "fill-[var(--sun)] text-[var(--sun)]" : "text-[#e5dfe3]"} ${onPick ? "cursor-pointer hover:scale-125 transition-transform" : ""}`} />
      ))}
    </span>
  );
}

export const sections = (text = "") =>
  text.split(/\n(?=## )/).map((b) => b.trim()).filter((b) => b.startsWith("## ")).map((b) => {
    const [h, ...rest] = b.split("\n");
    return { title: h.slice(3).replace(/^\d+\.\s*/, ""), body: rest.join("\n").trim() };
  });

export function Markdown({ text = "" }) {
  const blocks = text.split(/\n(?=## )|\n{2,}/).filter((b) => b.trim());
  return (
    <div className="prose-mps">
      {blocks.map((b, i) => {
        if (b.startsWith("## ")) {
          const [h, ...rest] = b.split("\n");
          return (
            <div key={i}>
              <h2>{h.slice(3)}</h2>
              {rest.join("\n").trim() && <p className="whitespace-pre-line">{rest.join("\n").trim()}</p>}
            </div>
          );
        }
        return <p key={i} className="whitespace-pre-line">{b.trim()}</p>;
      })}
    </div>
  );
}

export function Dots({ className = "" }) {
  return (
    <div className={`pointer-events-none absolute inset-0 ${className}`} aria-hidden>
      <span className="absolute top-[12%] left-[6%] w-3 h-3 rounded-full bg-[var(--pink)] opacity-70 floaty" />
      <span className="absolute top-[22%] right-[8%] w-4 h-4 rotate-45 bg-[var(--teal)] opacity-60 floaty" style={{ animationDelay: "1s" }} />
      <span className="absolute bottom-[16%] left-[12%] w-3 h-3 rounded-full bg-[var(--sun)] floaty" style={{ animationDelay: "2s" }} />
      <span className="absolute bottom-[10%] right-[16%] w-2.5 h-2.5 rounded-full bg-[var(--lilac)] floaty" style={{ animationDelay: ".5s" }} />
    </div>
  );
}

export function SectionTitle({ eyebrow, title, right }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-10">
      <div>
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h2 className="text-3xl sm:text-4xl font-semibold"><span className="squiggle">{title}</span></h2>
      </div>
      {right}
    </div>
  );
}

export function ProductCard({ p, i = 0 }) {
  const { addProduct } = useCart();
  const free = !p.price;
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.07 }}
      className="group bg-white rounded-[28px] border border-[#f6e3ec] p-3 hover:shadow-[0_20px_40px_-20px_rgba(212,86,140,.35)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-testid={`product-card-${p.slug}`}>
      <Link to={`/produkt/${p.slug}`} className="block relative rounded-[22px] overflow-hidden bg-[var(--blush)] aspect-square">
        <img src={img(p.images?.[0])} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {free && <span className="absolute top-3 left-3 bg-[var(--teal)] text-white text-xs font-bold px-3 py-1 rounded-full">ZDARMA</span>}
        {p.is_new && !free && <span className="absolute top-3 left-3 bg-[var(--sun)] text-[var(--ink)] text-xs font-bold px-3 py-1 rounded-full">Novinka</span>}
      </Link>
      <div className="px-2 pt-4 pb-2">
        <Link to={`/produkt/${p.slug}`} className="font-display text-lg leading-tight line-clamp-2 min-h-[2.6rem] hover:text-[var(--pink)] transition-colors">{p.name}</Link>
        <div className="flex items-center gap-2 mt-1 h-5">{p.rating_count > 0 && <><Stars value={p.rating_avg} /><span className="text-xs text-[var(--muted)]">({p.rating_count})</span></>}</div>
        <div className="flex items-center justify-between gap-2 mt-3">
          <div className="min-w-0 whitespace-nowrap">
            {p.sale_price && <span className="text-xs line-through text-[var(--muted)] mr-1">{money(p.price)}</span>}
            <span className="font-extrabold text-lg">{free ? "Zdarma" : money(p.sale_price || p.price)}</span>
          </div>
          {free ? (
            <Link to={`/produkt/${p.slug}`} className="btn-ghost shrink-0 !p-0 w-11 h-11 sm:w-12 sm:h-12 !rounded-full grid place-items-center" aria-label="Získat zdarma" title="Získat zdarma" data-testid={`product-card-free-${p.slug}`}><Gift className="w-5 h-5" /></Link>
          ) : (
            <button onClick={() => addProduct(p)} className="btn-pink shrink-0 !p-0 w-11 h-11 sm:w-12 sm:h-12 !rounded-full grid place-items-center" aria-label="Přidat do košíku" title="Přidat do košíku" data-testid={`product-card-add-${p.slug}`}><ShoppingBag className="w-5 h-5" /></button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export function Empty({ children }) {
  return <div className="text-center py-16 text-[var(--muted)] font-hand text-xl">{children}</div>;
}
